import { type Raster, RGBA_CHANNELS, rasterFrom } from './raster';

/**
 * 브라우저 캔버스처럼 RGBA 를 premultiplied 8비트로 담는 버퍼다. 색 채널은 알파를 곱한 값이다.
 * 저장할 때마다 반올림(.5 는 짝수)하므로 마플 캔버스 합성의 8비트 오차를 그대로 재현한다.
 */
export interface PremulCanvas {
  readonly width: number;
  readonly height: number;
  readonly data: Uint8ClampedArray;
}

/** 캔버스 합성 연산. 이름은 Canvas 2D `globalCompositeOperation` 과 같다. */
export type CompositeOp = 'source-over' | 'destination-in' | 'destination-over' | 'multiply';

/** 정수 픽셀 위치. */
export interface PixelOffset {
  readonly x: number;
  readonly y: number;
}

const MAX_CHANNEL = 255;

/** 직선 RGBA 를 캔버스에 넣을 때처럼 premultiplied 8비트로 저장한다. */
export function toPremulCanvas(raster: Raster): PremulCanvas {
  const data = new Uint8ClampedArray(raster.data.length);
  for (let offset = 0; offset < data.length; offset += RGBA_CHANNELS) {
    const alpha = raster.data[offset + 3] ?? 0;
    for (let channel = 0; channel < 3; channel += 1) {
      data[offset + channel] = ((raster.data[offset + channel] ?? 0) * alpha) / MAX_CHANNEL;
    }
    data[offset + 3] = alpha;
  }
  return { width: raster.width, height: raster.height, data };
}

/**
 * 캔버스에서 픽셀을 꺼낼 때(getImageData·toDataURL)처럼 직선 RGBA 로 되돌린다. 알파가 0 이면 색도 0 이다.
 * 255 를 넘는 값은 Uint8ClampedArray 가 255 로 자른다.
 */
export function toStraightRaster(canvas: PremulCanvas): Raster {
  const data = new Uint8ClampedArray(canvas.data.length);
  for (let offset = 0; offset < data.length; offset += RGBA_CHANNELS) {
    const alpha = canvas.data[offset + 3] ?? 0;
    if (alpha === 0) continue;
    for (let channel = 0; channel < 3; channel += 1) {
      data[offset + channel] = ((canvas.data[offset + channel] ?? 0) * MAX_CHANNEL) / alpha;
    }
    data[offset + 3] = alpha;
  }
  return rasterFrom(canvas.width, canvas.height, data);
}

/**
 * 직선 RGBA 버퍼를 캔버스에 저장했다 꺼낸 값으로 제자리에서 바꾼다(toPremulCanvas → toStraightRaster 와 같은 값).
 * 두 번 거쳐도 결과가 같다. 중간 버퍼를 만들지 않으므로, 호출자가 방금 만든 자기 출력 버퍼에만 쓴다.
 */
export function roundTripOwnedBuffer(data: Uint8ClampedArray): void {
  const rounded = new Uint8ClampedArray(1);
  for (let offset = 0; offset < data.length; offset += RGBA_CHANNELS) {
    const alpha = data[offset + 3] ?? 0;
    for (let channel = 0; channel < 3; channel += 1) {
      if (alpha === 0) {
        data[offset + channel] = 0;
        continue;
      }
      rounded[0] = ((data[offset + channel] ?? 0) * alpha) / MAX_CHANNEL;
      data[offset + channel] = ((rounded[0] ?? 0) * MAX_CHANNEL) / alpha;
    }
  }
}

/**
 * source 를 at 위치에 op 연산으로 그린 새 캔버스를 돌려준다. 입력 캔버스는 바꾸지 않는다.
 * Canvas 2D 처럼 캔버스 전체에 연산하므로, source 가 덮지 않은 곳은 투명한 source 로 본다.
 * 그래서 destination-in 은 source 밖을 지우고 나머지 연산은 source 밖을 그대로 둔다.
 * 위치가 정수가 아니면 RangeError 다.
 */
export function drawRaster(
  canvas: PremulCanvas,
  source: Raster,
  at: PixelOffset,
  op: CompositeOp,
): PremulCanvas {
  if (!Number.isInteger(at.x) || !Number.isInteger(at.y)) {
    throw new RangeError(`그릴 위치 (${at.x}, ${at.y}) 가 정수가 아니다`);
  }
  const data = new Uint8ClampedArray(canvas.data.length);
  for (let y = 0; y < canvas.height; y += 1) {
    for (let x = 0; x < canvas.width; x += 1) {
      const offset = (y * canvas.width + x) * RGBA_CHANNELS;
      const sourceOffset = sourceOffsetAt(source, x - at.x, y - at.y);
      compositePixel(op, canvas.data, source.data, offset, sourceOffset, data);
    }
  }
  return { width: canvas.width, height: canvas.height, data };
}

/** source 안의 (sx, sy) 픽셀 오프셋. source 밖이면 -1 이다. */
function sourceOffsetAt(source: Raster, sx: number, sy: number): number {
  const inside = sx >= 0 && sy >= 0 && sx < source.width && sy < source.height;
  return inside ? (sy * source.width + sx) * RGBA_CHANNELS : -1;
}

/**
 * premultiplied 합성식 co = Fa·cs + Fb·cb (+ multiply 혼합 항), αo = Fa·αs + Fb·αb (+ αs·αb).
 * source 는 직선 RGBA 를 소수 그대로 premultiply 해서 쓰고, 결과만 8비트로 저장한다.
 */
function compositePixel(
  op: CompositeOp,
  backdrop: Uint8ClampedArray,
  source: Uint8ClampedArray,
  offset: number,
  sourceOffset: number,
  target: Uint8ClampedArray,
): void {
  const sourceAlpha = sourceOffset < 0 ? 0 : (source[sourceOffset + 3] ?? 0) / MAX_CHANNEL;
  const backdropAlpha = (backdrop[offset + 3] ?? 0) / MAX_CHANNEL;
  const [sourceFactor, backdropFactor] = porterDuffFactors(op, sourceAlpha, backdropAlpha);
  const isMultiply = op === 'multiply';
  for (let channel = 0; channel < 3; channel += 1) {
    const sourceColor = sourceOffset < 0 ? 0 : (source[sourceOffset + channel] ?? 0) * sourceAlpha;
    const backdropColor = backdrop[offset + channel] ?? 0;
    const blend = isMultiply ? (sourceColor * backdropColor) / MAX_CHANNEL : 0;
    target[offset + channel] = sourceFactor * sourceColor + backdropFactor * backdropColor + blend;
  }
  const blendAlpha = isMultiply ? sourceAlpha * backdropAlpha : 0;
  target[offset + 3] =
    (sourceFactor * sourceAlpha + backdropFactor * backdropAlpha + blendAlpha) * MAX_CHANNEL;
}

function porterDuffFactors(
  op: CompositeOp,
  sourceAlpha: number,
  backdropAlpha: number,
): readonly [number, number] {
  switch (op) {
    case 'source-over':
      return [1, 1 - sourceAlpha];
    case 'destination-in':
      return [0, sourceAlpha];
    case 'destination-over':
      return [1 - backdropAlpha, 1];
    case 'multiply':
      return [1 - backdropAlpha, 1 - sourceAlpha];
  }
}
